import { ActionFunction, redirect } from 'react-router-dom';
import { Store } from 'redux';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { formNaming } from '../../../../../components/forms/NewCommunicationForm/constants';

// Selector
import { getCurrentIndexTabFormByTab } from '../../../../../state/selectors/ui/tabs';
import { selectedOT } from '../../../../../state/selectors/ui/modal';
// Store

import { RootState } from '../../../../../state/configureStore';
// Actions
import { passData } from './ActionUtils/passData';
import { saveData } from './ActionUtils/saveData';
import { tabsType } from '../../../../../state/reducers/ui/tabs';
import { indexTabFormReset } from '../../../../../state/actions/ui/tabs';
// Utils
import { getModalKeyByTab } from '../../../../../utils/getModalKeyByTab';
import { isOfflineQueuedError } from '../../../../../offline/isOfflineQueuedError';
// Mismo resolutor que el camino online (`ActionUtils/saveData.ts`). Cuando
// eran dos funciones distintas, ambas llevaban cableado el mismo literal a
// Informes y arreglar una sola habría dejado el offline apuntando a una ruta
// que ya no existe.
import {
  parentFromSelection,
  resolveEditCommunicationRedirect,
} from './ActionUtils/resolveRedirect';
import { showSuccessToast } from '../../../../../utils/toasts';
import i18n from '../../../../../i18n';

function cleanupEditSessionStorage() {
  sessionStorage.removeItem(formNaming.EDIT_COMMUNICATION_FORM);
  sessionStorage.removeItem(formNaming.EDIT_COMMUNICATION_FORM_OT);
  sessionStorage.removeItem(formNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN);
  sessionStorage.removeItem(formNaming.COMMUNICATION_NAVIGATION_CONTEXT);
}

export const EditCommunicationSummary: (
  store: Store<RootState>
) => ActionFunction =
  (store) =>
  async ({ request, params }) => {
    const formData = await request.formData();
    const intent = formData.get('intent');

    // Qué comunicación se está editando lo dice la URL (`edit-communication/:id`,
    // que es el id de la Order gemela), no el componente: en el paso Resumen no
    // hay loader y `passData` no devuelve `id`, así que pedírselo a la página
    // daba vacío y el redirect caía al listado en vez de al detalle. Cazado en
    // la validación en navegador del 2026-08-05.
    if (params.id) formData.set('communicationOrderId', params.id);

    const tabSelected = getCurrentIndexTabFormByTab(
      tabsType.EDIT_COMMUNICATION
    )(store.getState() as RootState);

    console.log('[EditCommunicationSummary] Tab selected:', tabSelected);

    // Get the correct modal key based on tab index
    const modalKey = getModalKeyByTab(formNaming.EDIT_COMMUNICATION_FORM, tabSelected);

    console.log('[EditCommunicationSummary] Using modal key:', modalKey);

    const storedDataOTSelected = selectedOT(modalKey)(
      store.getState() as RootState
    );

    console.log('[EditCommunicationSummary] Stored OT data:', storedDataOTSelected);

    if (intent === 'pass-data') {
      try {
        return passData({
          formData,
          storedDataOTSelected,
        });
      } catch (err) {
        Sentry.captureException(err);
        console.log('Error:', err);
      }
    }
    if (intent === 'save-data') {
      try {
        return await saveData({
          formData,
          store,
          tabSelected,
          storedDataOTSelected,
          params,
        });
      } catch (err) {
        if (isOfflineQueuedError(err)) {
          showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
          // ANTES de cleanupEditSessionStorage: la resolución lee el contexto
          // de navegación que esa función borra.
          const redirectTarget = resolveEditCommunicationRedirect(
            formData,
            parentFromSelection(tabSelected, storedDataOTSelected)
          );
          cleanupEditSessionStorage();
          store.dispatch(indexTabFormReset(tabsType.EDIT_COMMUNICATION));
          window.dispatchEvent(
            new CustomEvent('offline-communication-queued')
          );
          return redirect(redirectTarget);
        }
        Sentry.captureException(err);
        console.log('> CommunicationSummary - error editing:', err);
        // Re-lanzar → errorElement de la ruta. Antes: `return null` silencioso
        // (el guardado fallaba y el usuario se quedaba en el form sin señal —
        // clase del incidente At.0001979.0008.02).
        throw err;
      }
    }
    return null;
  };
